import { useEffect, useRef, useState } from "react";
import { Icon } from "../ui/Icon";
import { NotificationDropdown } from "./NotificationDropdown";
import { useNotificationStore } from "../../stores/notification-store";
import { useUnreadCount } from "../../hooks/useNotifications";
import BellLinear from "~icons/solar/bell-linear";
import BellBingBold from "~icons/solar/bell-bing-bold";

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const setUnreadCount = useNotificationStore((s) => s.setUnreadCount);

  // Fetch the baseline unread count on mount (polls every 60s as SSE fallback)
  const { data: unreadData } = useUnreadCount();
  useEffect(() => {
    if (unreadData?.unread_count != null) {
      setUnreadCount(unreadData.unread_count);
    }
  }, [unreadData, setUnreadCount]);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const badgeLabel =
    unreadCount > 9 ? "9+" : unreadCount > 0 ? String(unreadCount) : null;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className="relative flex size-10 items-center justify-center rounded-full text-text-primary transition-colors hover:bg-bg-page focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        aria-label={
          unreadCount > 0
            ? `Notifications — ${unreadCount} unread`
            : "Notifications"
        }
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <Icon
          icon={unreadCount > 0 ? BellBingBold : BellLinear}
          size="md"
          decorative
        />

        {badgeLabel !== null && (
          <span
            className="absolute -right-0.5 -top-0.5 flex min-w-[18px] items-center justify-center rounded-full bg-error px-1 py-0.5 text-[10px] font-semibold leading-none text-white"
            aria-hidden="true"
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {isOpen && <NotificationDropdown onClose={() => setIsOpen(false)} />}
    </div>
  );
}
